import React, { useEffect, useState } from "react";
import { PlayCircle, Mic, Send, Radio, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ShareMenu } from "../components/live/PodcastLayer";

const HERO = "https://images.unsplash.com/photo-1712932115370-4f9d354ced6b?w=1600";

// YouTube / Vimeo share links can't be iframed directly — convert to embed URLs.
function toEmbedUrl(url) {
  const yt = /(?:youtube\.com\/(?:watch\?v=|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/.exec(url);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = /vimeo\.com\/(\d+)/.exec(url);
  if (vm && !url.includes("player.vimeo.com")) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export default function PodcastPage() {
  const [eps, setEps] = useState([]);
  const [topics, setTopics] = useState([]);
  const [form, setForm] = useState({ couple_names: "", email: "", story: "" });
  const [busy, setBusy] = useState(false);

  const [shows, setShows] = useState([]);
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const delEp = (id) => window.confirm("Delete this episode for good?") && api.delete(`/podcast/episodes/${id}`).then(() => { setEps((x) => x.filter((e) => e.episode_id !== id)); toast.success("Episode deleted"); }).catch((e) => toast.error(formatApiError(e)));
  const delShow = (id) => window.confirm("Delete this live show?") && api.delete(`/admin/podcast/scheduled/${id}`).then(() => { setShows((x) => x.filter((e) => e.show_id !== id)); toast.success("Show deleted"); }).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    api.get("/podcast/episodes").then((r) => setEps(r.data)).catch(() => {});
    api.get("/podcast/topics").then((r) => setTopics(r.data)).catch(() => {});
    api.get("/podcast/scheduled").then((r) => setShows((r.data || []).filter((s) => s.mode === "live"))).catch(() => {});
  }, []);

  async function submitStory(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/podcast/submit-story", form);
      toast.success("Story submitted — Mr. & Mrs. Don will be in touch.");
      setForm({ couple_names: "", email: "", story: "" });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div className="bg-[var(--bg)]">
      <Seo
        title="Locked In Lessons — Wedding & Marriage Podcast"
        description="Real talk on weddings, money, communication, and marriage with Mr. & Mrs. Don. The podcast couples actually share."
        path="/podcast"
        keywords={["wedding podcast", "marriage podcast", "couples podcast", "best marriage podcast", "relationship advice podcast"]}
      />
      <Navbar />
      <section className="relative bg-[var(--ink)] text-white">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative container-wide px-6 md:px-12 py-24 text-center">
          <p className="font-script text-[var(--gold)] text-5xl mb-2">Locked In Lessons</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl">with Mr. & Mrs. Don</h1>
          <p className="text-white/70 max-w-2xl mx-auto mt-6">Real talk. Real love. Real advice. From proposal stories to building an empire — for the journey ahead.</p>
        </div>
      </section>

      {shows.length > 0 && (
        <section className="pt-16" data-testid="podcast-live-shows">
          <div className="container-wide px-6">
            <p className="font-script text-[var(--gold)] text-3xl text-center mb-6">Live &amp; upcoming shows</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shows.map((s) => (
                <Link key={s.show_id} to={`/podcast/live/${s.show_id}`} data-testid={`podcast-show-${s.show_id}`} className="group bg-white border border-[var(--line)] rounded-2xl p-5 hover:border-[var(--gold)] hover:-translate-y-0.5 transition-[border-color,transform] duration-300">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${s.status === "live" ? "bg-red-600 text-white" : "bg-[var(--bg-warm)]"}`}>
                    <Radio className={`w-3 h-3 ${s.status === "live" ? "animate-pulse" : ""}`} /> {s.status === "live" ? "Live now" : new Date(s.scheduled_at).toLocaleString()}
                  </span>
                  <h3 className="font-serif text-xl mt-3">{s.title}</h3>
                  {s.description && <p className="text-sm text-[var(--muted)] mt-1 line-clamp-2">{s.description}</p>}
                  <p className="text-xs text-[var(--gold)] mt-3 uppercase tracking-wider">{s.status === "live" ? "Join the show" : "Get ready"} →</p>
                  {admin && <button onClick={(e) => { e.preventDefault(); delShow(s.show_id); }} className="mt-3 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`podcast-public-del-show-${s.show_id}`}><Trash2 className="w-3 h-3" />Delete</button>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section-pad">
        <div className="container-wide px-6">
          <p className="font-script text-[var(--gold)] text-3xl text-center mb-2">Latest episodes</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-center mb-12">Every episode, a new lesson.</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eps.map((ep) => (
              <div key={ep.episode_id} id={`ep-${ep.episode_id}`} className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden hover:border-[var(--gold)] transition-colors">
                {ep.video_url ? (
                  ep.video_url.startsWith("http") && !ep.video_url.includes("/api/files/") ? (
                    <div className="aspect-video bg-black"><iframe src={toEmbedUrl(ep.video_url)} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title={ep.title} /></div>
                  ) : (
                    <video controls className="w-full aspect-video bg-black" poster={ep.thumbnail_url || undefined}><source src={ep.video_url} /></video>
                  )
                ) : (
                  <div className="aspect-video bg-[var(--bg-warm)]">
                    {ep.thumbnail_url ? <img src={ep.thumbnail_url} alt={ep.title} className="w-full h-full object-cover" /> : null}
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3"><ShareMenu url={`${window.location.origin}/podcast#ep-${ep.episode_id}`} title={ep.title} dark={false} />
                    {admin && <button onClick={() => delEp(ep.episode_id)} className="ml-auto p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50" aria-label="Delete episode" data-testid={`podcast-public-del-${ep.episode_id}`}><Trash2 className="w-4 h-4" /></button>}</div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">Episode {ep.episode_number} · {ep.duration_min} min · {ep.topic}</p>
                  <h3 className="font-serif text-xl mb-2">{ep.title}</h3>
                  <p className="text-sm text-[var(--muted)] line-clamp-3 mb-4">{ep.description}</p>
                  {ep.audio_url && (
                    <audio controls className="w-full" data-testid={`audio-ep-${ep.episode_number}`}><source src={ep.audio_url} /></audio>
                  )}
                  {!ep.audio_url && !ep.video_url && (
                    <button data-testid={`play-ep-${ep.episode_number}`} className="text-sm flex items-center gap-2 text-[var(--gold)]"><PlayCircle className="w-4 h-4" /> Coming soon</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-warm)] section-pad">
        <div className="container-wide px-6 text-center">
          <p className="font-script text-[var(--gold)] text-3xl">Browse</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl mb-10">Topics</h2>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {topics.map((t) => (
              <span key={t.slug} className="text-xs uppercase tracking-[0.16em] px-5 py-3 border border-[var(--gold-line)] rounded-full text-[var(--ink)] bg-white">{t.label}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-wide px-6 max-w-2xl">
          <div className="text-center mb-10">
            <Mic className="w-6 h-6 mx-auto text-[var(--gold)] mb-3" />
            <p className="font-script text-[var(--gold)] text-3xl">Submit your love story</p>
            <h2 className="font-serif font-light text-4xl">Be featured on the show</h2>
            <p className="text-[var(--muted)] mt-3">Real couples. Real stories. Mr. & Mrs. Don want to hear yours.</p>
          </div>
          <form onSubmit={submitStory} className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Couple names</label>
              <input data-testid="story-names" required value={form.couple_names} onChange={(e) => setForm({ ...form, couple_names: e.target.value })} className="input-luxe" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Email</label>
              <input data-testid="story-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-luxe" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Your story</label>
              <textarea data-testid="story-text" required rows={5} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} className="input-luxe resize-none" />
            </div>
            <button data-testid="story-submit" disabled={busy} type="submit" className="btn-gold"><Send className="w-4 h-4" /> {busy ? "Sending…" : "Submit Story"}</button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
